import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccountFilterProvider } from './contexts/AccountFilterContext';
import { accountsAPI } from './services/api';
import TestNavigation from './components/TestNavigation';
import SideNavigation from './components/SideNavigation';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import Liabilities from './pages/Liabilities';
import BankConnections from './pages/BankConnections';
import CashFlow from './pages/CashFlow';
import NavigationTest from './pages/NavigationTest';
import CardStylesDemo from './pages/CardStylesDemo';
import DataImport from './components/DataImport';
import Profile from './pages/Profile';
import Login from './components/Login';
import CategorizationManagement from './pages/CategorizationManagement';
import BusinessAccountingMapping from './pages/BusinessAccountingMapping';
import Transactions from './pages/Transactions';
import Onboarding from './pages/Onboarding';
import './index.css';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Gates the dashboard: redirects to onboarding if user has no accounts yet.
// Users who already have accounts get the flag set automatically so they're never interrupted again.
const OnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('wealthini_onboarding_complete') === 'true') {
      setReady(true);
      return;
    }
    accountsAPI.getAccounts({}).then((accounts) => {
      if (accounts.length > 0) {
        // Existing user with data — mark done so we never check again
        localStorage.setItem('wealthini_onboarding_complete', 'true');
        setReady(true);
      } else {
        setRedirect(true);
      }
    }).catch(() => {
      // On error, let them through rather than redirect-looping
      setReady(true);
    });
  }, []);

  if (redirect) return <Navigate to="/onboarding" replace />;
  if (!ready) return (
    <div className="loading-container dark-theme">
      <div className="loading-spinner"></div>
      <p className="loading-text">Loading...</p>
    </div>
  );
  return <>{children}</>;
};

// Main App Layout
const AppLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  // Initialize theme on app load
  useEffect(() => {
    // Check for saved theme preference or default to light
    const savedTheme = localStorage.getItem('theme') || 'light';
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('dark-theme', 'light-theme');
    
    // Add the saved theme class
    root.classList.add(`${savedTheme}-theme`);
    root.setAttribute('data-theme', savedTheme);
    
    console.log(`Initial theme applied: ${savedTheme}`);
  }, []);

  // Use SideNavigation only on the side-nav test page(s)
  const useSideNavigation = false;

  const isOnboardingRoute = location.pathname === '/onboarding';

  return (
    <div className="app">
      {isAuthenticated && user && !isOnboardingRoute && (
        useSideNavigation ? (
          <SideNavigation onLogout={logout} user={user} />
        ) : (
          <TestNavigation onLogout={logout} user={user} />
        )
      )}
              <main className={`main-content ${useSideNavigation ? 'ml-64' : ''}`}>
          <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <Dashboard />
                </OnboardingGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <Assets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/liabilities"
            element={
              <ProtectedRoute>
                <Liabilities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/banks"
            element={
              <ProtectedRoute>
                <BankConnections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cash-flow"
            element={
              <ProtectedRoute>
                <CashFlow />
              </ProtectedRoute>
            }
          />
            <Route
              path="/nav-test"
              element={
                <ProtectedRoute>
                  <NavigationTest />
                </ProtectedRoute>
              }
            />
            <Route path="/card-styles" element={<CardStylesDemo />} />
          <Route
            path="/import"
            element={
              <ProtectedRoute>
                <DataImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categorization"
            element={
              <ProtectedRoute>
                <CategorizationManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-accounting"
            element={
              <ProtectedRoute>
                <BusinessAccountingMapping />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Transactions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                {localStorage.getItem('wealthini_onboarding_complete') === 'true' &&
                 localStorage.getItem('wealthini_force_onboarding') !== 'true'
                  ? <Navigate to="/" replace />
                  : <Onboarding />}
              </ProtectedRoute>
            }
          />
          {/* Catch all route - redirect to dashboard if authenticated, login if not */}
          <Route
            path="*"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </main>
    </div>
  );
};

// Root App component with AuthProvider
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AccountFilterProvider>
          <AppLayout />
        </AccountFilterProvider>
      </AuthProvider>
    </Router>
  );
};

export default App; 